import React, { useState } from 'react';
import { useDatabase } from '../../hooks/useDatabase';
import { Button, Badge } from '../ui';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { generateSecureCode } from '../../utils/codeGenerator';

const IN_USE_STATES = ['activo', 'libre', 'pendiente', 'bloqueado'];

const CodeManager = ({ clienteId, trasteroId }) => {
  const { data: codigos, insert, update } = useDatabase('codigos');
  const [loading, setLoading] = useState(false);

  if (!codigos) return null;

  const myCodes = codigos.filter(c => c.cliente_id === clienteId);
  const currentCode = myCodes.find(c => c.estado === 'activo' || c.estado === 'pendiente');

  const executeAction = async (actionFn) => {
    setLoading(true);
    try {
      await actionFn();
    } catch (err) {
      alert('Error en el código de acceso: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = () => executeAction(async () => {
    const libreCode = trasteroId ? codigos.find(c => c.trastero_id === trasteroId && c.estado === 'libre') : null;

    if (libreCode) {
      await update(libreCode.id, {
        cliente_id: clienteId,
        estado: 'activo',
        fecha_activacion: new Date().toISOString(),
        sincronizacion_puerta: 'pendiente_alta'
      });
    } else {
      const activeCodesList = codigos
        .filter(c => IN_USE_STATES.includes(c.estado))
        .map(c => c.codigo);
      const newCode = generateSecureCode(activeCodesList);

      await insert({
        codigo: newCode,
        cliente_id: clienteId,
        trastero_id: trasteroId || null,
        estado: 'activo',
        fecha_activacion: new Date().toISOString(),
        sincronizacion_puerta: 'pendiente_alta'
      });
    }
  });

  const requestManualCode = () => {
    const choice = prompt('Introduce el código manual (ej: 123456):');
    if (!choice) return;
    
    const newCode = choice.trim();
    if (newCode.length < 4) {
      alert('El código debe tener al menos 4 caracteres.');
      return;
    }

    const existingActive = codigos.find(c => c.codigo === newCode && IN_USE_STATES.includes(c.estado));

    executeAction(async () => {
      if (existingActive) {
        if (existingActive.estado === 'libre' && (trasteroId && existingActive.trastero_id === trasteroId)) {
          if (currentCode) {
            await update(currentCode.id, {
              estado: 'sustituido',
              fecha_sustitucion: new Date().toISOString(),
              sincronizacion_puerta: 'pendiente_baja'
            });
          }
          await update(existingActive.id, {
            cliente_id: clienteId,
            estado: 'activo',
            fecha_activacion: new Date().toISOString(),
            sincronizacion_puerta: 'pendiente_alta'
          });
          return;
        } else {
          alert('Ese código ya está en uso actualmente en otro lugar. Debes darlo de baja primero.');
          return;
        }
      }

      if (currentCode) {
        await update(currentCode.id, {
          estado: 'sustituido',
          fecha_sustitucion: new Date().toISOString(),
          sincronizacion_puerta: 'pendiente_baja'
        });
      }
      
      await insert({
        codigo: newCode,
        cliente_id: clienteId,
        trastero_id: trasteroId || currentCode?.trastero_id || null,
        reserva_id: currentCode?.reserva_id || null,
        estado: 'activo',
        fecha_activacion: new Date().toISOString(),
        sincronizacion_puerta: 'pendiente_alta',
        notas_internas: 'Asignado manualmente'
      });
    });
  };

  const handleExpire = () => executeAction(async () => {
    if (!currentCode) return;
    await update(currentCode.id, {
      estado: 'expirado',
      fecha_expiracion: new Date().toISOString(),
      sincronizacion_puerta: 'pendiente_baja'
    });
  });

  const handleBlock = () => executeAction(async () => {
    if (!currentCode) return;
    await update(currentCode.id, {
      estado: 'bloqueado',
      sincronizacion_puerta: 'pendiente_baja'
    });
  });

  const handleChange = () => executeAction(async () => {
    if (currentCode) {
      await update(currentCode.id, {
        estado: 'sustituido',
        fecha_sustitucion: new Date().toISOString(),
        sincronizacion_puerta: 'pendiente_baja'
      });
    }
    const activeCodesList = codigos
      .filter(c => IN_USE_STATES.includes(c.estado))
      .map(c => c.codigo);
    const newCode = generateSecureCode(activeCodesList);

    await insert({
      codigo: newCode,
      cliente_id: clienteId,
      trastero_id: trasteroId || currentCode?.trastero_id || null,
      reserva_id: currentCode?.reserva_id || null,
      estado: 'activo',
      fecha_activacion: new Date().toISOString(),
      sincronizacion_puerta: 'pendiente_alta',
      notas_internas: 'Rotado manualmente desde ficha'
    });
  });

  const copyWhatsApp = () => {
    if (!currentCode) return;
    const msg = `Hola, tu código de acceso a Blue Blue On es: ${currentCode.codigo}. Este código es personal e intransferible. Por seguridad, no lo compartas con terceros.`;
    navigator.clipboard.writeText(msg);
    alert('Mensaje de WhatsApp copiado al portapapeles');
  };

  const copyCode = () => {
    if (!currentCode) return;
    navigator.clipboard.writeText(currentCode.codigo);
    alert('Código copiado al portapapeles');
  };

  if (!currentCode) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
        <p className="text-sm text-brand-text mb-3">Este cliente no tiene un código de acceso activo.</p>
        <div className="flex flex-col sm:flex-row gap-2 justify-center">
          <Button onClick={handleGenerate} disabled={loading} variant="outline" className="w-full sm:w-auto" type="button">
            <SafeIcon icon={FiIcons.FiKey} className="mr-2" />
            {loading ? 'Generando...' : 'Generar Aleatorio'}
          </Button>
          <Button onClick={requestManualCode} disabled={loading} variant="ghost" className="w-full sm:w-auto" type="button">
            <SafeIcon icon={FiIcons.FiEdit3} className="mr-2" />
            Asignar Manual
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm relative overflow-hidden">
      {loading && (
        <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] z-10 flex items-center justify-center">
          <SafeIcon icon={FiIcons.FiLoader} className="animate-spin text-brand-primary text-2xl" />
        </div>
      )}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-brand-text">Código Activo</span>
          {['pendiente_alta', 'pendiente_baja'].includes(currentCode.sincronizacion_puerta) && (
            <span className="flex h-2 w-2 relative" title="Pendiente de sincronizar en puerta">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-warning opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-warning"></span>
            </span>
          )}
        </div>
        <Badge variant={currentCode.estado}>{currentCode.estado}</Badge>
      </div>
      <div className="text-3xl sm:text-4xl font-mono font-bold text-center tracking-widest text-brand-dark mb-6">
        {currentCode.codigo}
      </div>
      <div className="flex flex-wrap gap-2 justify-center">
        <Button variant="ghost" onClick={copyCode} title="Copiar código" type="button" className="!px-3 bg-gray-50 flex-1 sm:flex-none">
          <SafeIcon icon={FiIcons.FiCopy} />
        </Button>
        <Button variant="ghost" onClick={copyWhatsApp} title="Copiar mensaje WhatsApp" type="button" className="!px-3 text-green-600 hover:text-green-700 hover:bg-green-50 bg-green-50/50 flex-1 sm:flex-none">
          <SafeIcon icon={FiIcons.FiMessageCircle} />
        </Button>
        <Button variant="ghost" onClick={handleChange} title="Rotar código (Aleatorio)" type="button" className="!px-3 text-blue-600 hover:text-blue-700 hover:bg-blue-50 bg-blue-50/50 flex-1 sm:flex-none">
          <SafeIcon icon={FiIcons.FiRefreshCw} />
        </Button>
        <Button variant="ghost" onClick={requestManualCode} title="Asignar código manual específico" type="button" className="!px-3 text-purple-600 hover:text-purple-700 hover:bg-purple-50 bg-purple-50/50 flex-1 sm:flex-none">
          <SafeIcon icon={FiIcons.FiEdit3} />
        </Button>
        <Button variant="ghost" onClick={handleBlock} title="Bloquear temporalmente" type="button" className="!px-3 text-red-600 hover:text-red-700 hover:bg-red-50 bg-red-50/50 flex-1 sm:flex-none">
          <SafeIcon icon={FiIcons.FiXOctagon} />
        </Button>
        <Button variant="ghost" onClick={handleExpire} title="Marcar como expirado (Baja)" type="button" className="!px-3 text-gray-600 hover:text-gray-700 hover:bg-gray-100 bg-gray-50 flex-1 sm:flex-none">
          <SafeIcon icon={FiIcons.FiClock} />
        </Button>
      </div>
    </div>
  );
};

export default CodeManager;